import React, { useState, useRef } from 'react';
import { Input, Button, Row, Col, Typography, Table, message, Card, Space, Divider } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Title } = Typography;

function parseTableFile(tableContent) {
  const lines = tableContent.split('\n').filter(Boolean);
  const fieldMap = {};
  lines.forEach((line) => {
    const match = line.match(/(\w+)\(([\w.]+)\)\s*=\s*(\d+)/);
    if (match) {
      const [_, field, fieldName, pos] = match;
      fieldMap[pos] = field;
    }
  });
  return fieldMap;
}

function parseRecord(recordStr, tableMap) {
  const FM = "^";
  const VM = "]";
  const SM = "\\";
  const fields = recordStr.split(FM);
  const result = {};

  fields.forEach((val, i) => {
    if (!val) return;
    const fieldName = tableMap[i + 1] || `Field ${i + 1}`;
    const vmSplit = val.split(VM);
    vmSplit.forEach((vmVal, vmIdx) => {
      const smSplit = vmVal.split(SM);
      smSplit.forEach((smVal, smIdx) => {
        const key = `${fieldName}.${vmIdx + 1}.${smIdx + 1}`;
        result[key] = smVal;
      });
    });
  });
  return result;
}

function RecordComparator() {
  const [tableInput, setTableInput] = useState('');
  const [record1, setRecord1] = useState('');
  const [record2, setRecord2] = useState('');
  const [rows, setRows] = useState([]);
  const [showInputs, setShowInputs] = useState(true);
  const [differences, setDifferences] = useState([]);
  const [currentDiffIndex, setCurrentDiffIndex] = useState(0);
  const tableRef = useRef(null);

  const handleCompare = () => {
    if (!tableInput || !record1 || !record2) {
      message.error('Please paste .table content, record 1, and record 2');
      return;
    }

    const tableMap = parseTableFile(tableInput);
    const rec1 = parseRecord(record1, tableMap);
    const rec2 = parseRecord(record2, tableMap);

    const data = [];
    const diffKeys = [];
    Object.keys(tableMap).forEach(pos => {
      const fieldName = tableMap[pos];
      const keys = Object.keys(rec1).filter(key => key.startsWith(fieldName)).concat(Object.keys(rec2).filter(key => key.startsWith(fieldName)));
      const uniqueKeys = Array.from(new Set(keys));
      uniqueKeys.forEach(key => {
        const val1 = rec1[key] || '';
        const val2 = rec2[key] || '';
        const isDiff = val1 !== val2;
        if (isDiff) diffKeys.push(key);
        data.push({
          key,
          field: key,
          record1: val1,
          record2: val2,
          isDiff,
        });
      });
    });
    setRows(data);
    setDifferences(diffKeys);
    setCurrentDiffIndex(0);
    setShowInputs(false);
  };

  const scrollToDifference = (direction) => {
    if (differences.length === 0) return;
    let nextIndex = currentDiffIndex;
    if (direction === 'up') {
      nextIndex = Math.max(0, currentDiffIndex - 1);
    } else if (direction === 'down') {
      nextIndex = Math.min(differences.length - 1, currentDiffIndex + 1);
    }
    setCurrentDiffIndex(nextIndex);
    const nextKey = differences[nextIndex];
    const element = document.querySelector(`[data-row-key="${nextKey}"]`);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const columns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      render: text => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Record 1',
      dataIndex: 'record1',
      key: 'record1',
      render: (text, record) => (
        <span style={{ backgroundColor: record.isDiff ? '#ffccc7' : 'transparent', padding: '5px', borderRadius: '4px' }}>{text}</span>
      )
    },
    {
      title: 'Record 2',
      dataIndex: 'record2',
      key: 'record2',
      render: (text, record) => (
        <span style={{ backgroundColor: record.isDiff ? '#d6f5ff' : 'transparent', padding: '5px', borderRadius: '4px' }}>{text}</span>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5' }}>
      <Card style={{ marginBottom: '24px', backgroundColor: '#ffffff', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
        <Title level={3} style={{ textAlign: 'center', color: '#1890ff' }}>jBC Record Comparison Tool</Title>
        {showInputs ? (
          <Row gutter={16}>
            <Col span={8}>
              <Card style={{ backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                <Title level={5}>Paste .table content</Title>
                <TextArea rows={10} value={tableInput} onChange={e => setTableInput(e.target.value)} placeholder="FIELD1: 1\nFIELD2: 2" />
              </Card>
            </Col>
            <Col span={8}>
              <Card style={{ backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                <Title level={5}>Paste Record 1</Title>
                <TextArea rows={10} value={record1} onChange={e => setRecord1(e.target.value)} placeholder="Raw record with ^, ] and \" />
              </Card>
            </Col>
            <Col span={8}>
              <Card style={{ backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                <Title level={5}>Paste Record 2</Title>
                <TextArea rows={10} value={record2} onChange={e => setRecord2(e.target.value)} placeholder="Raw record with ^, ] and \" />
              </Card>
            </Col>
          </Row>
        ) : (
          <Card onClick={() => setShowInputs(true)} style={{ cursor: 'pointer', color: '#1890ff', marginBottom: '16px', textAlign: 'center', backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
            Click here to edit inputs
          </Card>
        )}
        <Button type="primary" style={{ marginBottom: '16px', width: '100%' }} onClick={handleCompare}>Compare</Button>
      </Card>

      {!showInputs && (
        <Card style={{ marginBottom: '24px', backgroundColor: '#ffffff', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
          {differences.length > 0 ? (
            <Space style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Button icon={<UpOutlined />} onClick={() => scrollToDifference('up')} disabled={differences.length === 0}>Up</Button>
              <Button icon={<DownOutlined />} onClick={() => scrollToDifference('down')} disabled={differences.length === 0}>Down</Button>
              <span>Differences: {differences.length}</span>
            </Space>
          ) : (
            <div style={{ textAlign: 'center', color: '#1890ff' }}>No differences found</div>
          )}
        </Card>
      )}

      {rows.length > 0 && (
        <Card style={{ backgroundColor: '#ffffff', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
          <Table
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ y: 500 }}
            bordered
            rowClassName={(record) => record.isDiff ? 'diff-row' : ''}
            ref={tableRef}
            style={{ backgroundColor: '#f0f2f5' }}
          />
        </Card>
      )}
    </div>
  );
}

export default RecordComparator;
